import { PromotionalEventType } from "@faire/web-api--source/indigofair/common/PromotionalEventType";
import { ICurrentPromotionalEventResponse } from "@faire/web-api--source/indigofair/data/ICurrentPromotionalEventResponse";
import { useClock } from "@faire/web/common/useClock";
import { FaireMarketTheme } from "@faire/web/slate/Theme/EventsTheme";
import { toZonedTime } from "date-fns-tz";
import { isAfter } from "date-fns/isAfter";
import { useCallback, useMemo } from "react";

import { getMarketEventMatchedDiscountBps } from "../../../serialized-data/getMarketEventMatchedDiscountBps";
import { getNextOrActiveEvent } from "../../../serialized-data/getNextOrActiveEvent";
import { ILineItemStyle } from "../../../utils/lineItemStyle";

import PromotionalEventGroup = ICurrentPromotionalEventResponse.IPromotionalEvent.PromotionalEventGroup;

type EventType = keyof typeof PromotionalEventType;

export const useEventsStore = () => {
  const nextOrActive = getNextOrActiveEvent();
  const marketEventMatchedDiscountBps = getMarketEventMatchedDiscountBps();

  const isMarketEvent =
    nextOrActive?.event?.event_group === PromotionalEventGroup.MARKET;

  const eventLineItemStyle = isMarketEvent
    ? ILineItemStyle.MARKET
    : ILineItemStyle.FAIRE_FASHION_WEEK_DISCOUNT;

  const nextOrActiveEventTheme = useMemo(() => {
    switch (nextOrActive?.event?.event_group) {
      case PromotionalEventGroup.MARKET:
      default:
        return FaireMarketTheme;
    }
  }, [nextOrActive]);

  const eventData = useCallback(
    (eventType: EventType): ICurrentPromotionalEventResponse | undefined => {
      return nextOrActive?.event?.event_type === eventType
        ? nextOrActive
        : undefined;
    },
    [nextOrActive]
  );

  const { now } = useClock();

  const isEventExpired = useCallback(
    (eventType: EventType): boolean => {
      const eventData = nextOrActive;
      if (!eventData || eventData?.event?.event_type !== eventType) {
        return true;
      }
      const endAt = eventData.end_at;
      if (endAt === undefined) {
        // assuming an event with undefined end date always live
        return false;
      }
      return isAfter(now, endAt);
    },
    [nextOrActive, now]
  );

  return useMemo(
    () => ({
      nextOrActive,
      isMarketEvent,
      eventLineItemStyle,
      nextOrActiveEventTheme,
      eventData,
      isEventExpired,
      unsnapFromPT,
      marketEventMatchedDiscountBps,
    }),
    [
      nextOrActive,
      isMarketEvent,
      eventLineItemStyle,
      nextOrActiveEventTheme,
      eventData,
      isEventExpired,
      unsnapFromPT,
      marketEventMatchedDiscountBps,
    ]
  );
};

const PT_TIMEZONE = "America/Los_Angeles";

const unsnapFromPT = (timeUtc: number): number => {
  return toZonedTime(timeUtc, PT_TIMEZONE).valueOf();
};
